import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useConversations } from "../../hooks/useConversations";
import HistoryItem from "./HistoryItem";
import { dumontColors } from "../../colors";

export interface HistoryViewProps {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  onOpenConversation: (conversationId: string) => void;
}

export default function HistoryView({ searchQuery, setSearchQuery, onOpenConversation }: HistoryViewProps) {
  const { t: translate } = useTranslation();
  const { filterConversations, formatRelativeDate } = useConversations();
  const conversations = useMemo(() => filterConversations(searchQuery), [filterConversations, searchQuery]);

  const sorted = useMemo(
    () => conversations.slice().sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
    [conversations]
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1152px",
        margin: "24px auto 0",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 24px)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: "63px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px 18px 24px",
          borderBottom: "1px solid #EBEBEB",
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1D2939", fontFamily: "Manrope" }}>
          {translate("dumontConversationHistory")}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1 }}>
        {sorted.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center" }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: dumontColors.textTertiary, mb: 1, fontFamily: "Manrope" }}>
              {searchQuery ? translate("dumontNoConversationsFound") : translate("dumontNoConversationHistory")}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#98A2B3", fontFamily: "Manrope" }}>
              {searchQuery ? translate("dumontTryDifferentSearchTerm") : translate("dumontStartNewConversation")}
            </Typography>
          </Box>
        ) : (
          sorted.map((conversation) => (
            <HistoryItem
              key={conversation.id}
              conversation={conversation}
              subtitle={formatRelativeDate(conversation.updatedAt)}
              onClick={() => onOpenConversation(conversation.id)}
            />
          ))
        )}
      </Box>
    </Box>
  );
}


